import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react';

import { SidebarProvider } from '../ui/sidebar';

export type SidebarMode = 'collapsed' | 'expanded' | 'hover';

const STORAGE_KEY = 'sidebar_mode';
const MODES: SidebarMode[] = ['collapsed', 'expanded', 'hover'];

// Entering is quicker than leaving so a pointer crossing the rail on its way
// elsewhere doesn't flash it open, and a pointer leaving towards a menu item
// doesn't snap it shut underneath.
const HOVER_IN_DELAY = 50;
const HOVER_OUT_DELAY = 150;

const readMode = (): SidebarMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return MODES.find((mode) => mode === stored) ?? 'hover';
};

interface SidebarModeContextValue {
  mode: SidebarMode;
  setMode: (mode: SidebarMode) => void;
  onHoverChange: (entering: boolean) => void;
}

const SidebarModeContext = createContext<SidebarModeContextValue | null>(null);

export const useSidebarMode = () => {
  const context = useContext(SidebarModeContext);
  if (!context) {
    throw new Error(
      'useSidebarMode must be used within an AppSidebarProvider.',
    );
  }
  return context;
};

/**
 * SidebarProvider driven by a persisted three-way mode rather than a bare open flag.
 *
 * Deliberately ignores the `sidebar_state` cookie shadcn writes on every setOpen: it
 * records the *derived* open flag, so in hover mode it would record hovers.
 */
export const AppSidebarProvider = ({
  children,
  ...props
}: ComponentProps<typeof SidebarProvider>) => {
  const [mode, _setMode] = useState(readMode);
  const [hovered, setHovered] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const setMode = useCallback((next: SidebarMode) => {
    clearTimeout(timer.current);
    setHovered(false);
    _setMode(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  // ponytail: no-op on mobile, where the sidebar renders as a Sheet and there is
  // nothing to hover.
  const onHoverChange = useCallback(
    (entering: boolean) => {
      if (mode !== 'hover') return;
      clearTimeout(timer.current);
      timer.current = setTimeout(
        () => {
          setHovered(entering);
        },
        entering ? HOVER_IN_DELAY : HOVER_OUT_DELAY,
      );
    },
    [mode],
  );

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const value = useMemo(
    () => ({ mode, setMode, onHoverChange }),
    [mode, setMode, onHoverChange],
  );

  return (
    <SidebarModeContext.Provider value={value}>
      <SidebarProvider
        data-sidebar-mode={mode}
        open={mode === 'expanded' || (mode === 'hover' && hovered)}
        // Without this, Cmd/Ctrl+B is swallowed by the controlled `open`. Pinning
        // from the keyboard picks a mode like any other choice, and persists.
        onOpenChange={(open) => {
          setMode(open ? 'expanded' : 'collapsed');
        }}
        {...props}
      >
        {children}
      </SidebarProvider>
    </SidebarModeContext.Provider>
  );
};
